import { SUBSCRIPTION_STATUS } from '~/types/subscription';

/**
 * A single paid invoice. The list endpoint returns the basic row fields, and
 * the invoice preview/PDF loads the full payload from the detail endpoint.
 * `merchant*` fields are the seller (legal entity) block; `merchantTaxId` is
 * the VAT shown next to the legal address.
 */
export type BillingHistoryInvoice = {
  invoiceId: string;
  orderId: string;
  chargedAt: string;
  /** Charged amount in minor units (e.g. cents). Divide by 100 to display. */
  amountMinor: number;
  currency: string;
  description: string;
  customerEmail: string;
  paymentCardBrand: string;
  paymentCardLast4: string;
  merchantLegalName: string;
  merchantAddressFirstLine: string;
  merchantAddressRemainingLines: string[];
  merchantTaxId: string;
};

/**
 * Whether the user has or ever had a subscription. `NONE` (and a missing
 * status) means a free user who never purchased — they get no Billing History.
 * Any other status (active, cancelled, paused, redemption…) means a charge
 * happened at some point, so the tab is relevant. Matches the app-wide
 * convention that `status === NONE` is a non-paying user.
 */
export const hasOrHadSubscription = (status?: SUBSCRIPTION_STATUS): boolean =>
  status != null && status !== SUBSCRIPTION_STATUS.NONE;

const DEFAULT_LOCALE = 'en-US';

export const formatInvoiceAmount = (
  amountMinor: number,
  currency: string,
  locale: string = DEFAULT_LOCALE,
): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amountMinor / 100);

export const formatInvoiceDate = (
  iso: string,
  locale: string = DEFAULT_LOCALE,
): string =>
  new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));

/**
 * Amount as `39.99 USD` (decimal + ISO code) — the format used inside the PDF
 * invoice, matching the approved legal template. The in-app table uses
 * `formatInvoiceAmount` (locale currency symbol) instead.
 */
export const formatInvoiceAmountWithCode = (
  amountMinor: number,
  currency: string,
  locale: string = DEFAULT_LOCALE,
): string =>
  `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountMinor / 100)} ${currency}`;

/** Numeric `DD/MM/YYYY` date for the PDF invoice, matching the template. */
export const formatInvoiceDateNumeric = (iso: string): string => {
  const d = new Date(iso);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
};
